import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { twMerge } from 'tailwind-merge';
import { getAllowedRange } from '@/utils/dateUtils';
import { formatDateStringWithPreset } from '@/utils/dateUtils/dateFormatUtils';
import { findMatchingPreset, getDatePresets } from '@/utils/dateUtils/presets';
import { usePageMetadata } from '@/hooks';
import useAllTimePreset from '@/hooks/useAllTimePreset';
import type { DateRange } from '@/types';
import { DateString } from '@/types/state/filter';
import { isInRange } from '@/utils';

const DATE_FORMAT = 'YYYY-MM-DD';

interface DateRangeFilterProps {
  dateRange: DateRange;
  onApply: (start: DateString, end: DateString) => void;
  onOpen?: () => void;
  onPresetSelect?: (presetName: string) => void;
}

const toDateString = (day: Dayjs): DateString => day.format(DATE_FORMAT) as DateString;

/**
 * Echo date range filter with presets. Selectable calendar days match {@link getAllowedRange}. The trigger always shows the
 * preset-aware label (prefix + body) from formatDateStringWithPreset, not the native inputs.
 * Dates are `YYYY-MM-DD` strings throughout; the range applies as soon as antd reports a complete range.
 */
const DateRangeFilter = ({
  dateRange: { from, to },
  onApply,
  onOpen,
  onPresetSelect,
}: DateRangeFilterProps) => {
  const { page: pageType } = usePageMetadata();
  // TODO: remove once `allTimePreset` is enabled for everyone.
  const isAlltimePresetEnabled = useAllTimePreset();
  // Recomputed every render on purpose: it is cheap and stays correct if the tab is open past midnight.
  const presets = getDatePresets();
  const isDateDisabled = (current: Dayjs): boolean =>
    !isInRange(toDateString(current), getAllowedRange(isAlltimePresetEnabled));
  const selectedPreset = findMatchingPreset(from, to, presets);
  const { prefix, body } = formatDateStringWithPreset(from, to, pageType, selectedPreset);

  const rangePresets = presets.map(({ name, value: [start, end] }) => ({
    label: name,
    value: [dayjs(start), dayjs(end)] as [Dayjs, Dayjs],
  }));

  const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates[1]) {
      return;
    }

    const nextFrom = toDateString(dates[0]);
    const nextTo = toDateString(dates[1]);

    if (nextFrom === from && nextTo === to) {
      return;
    }

    onApply(nextFrom, nextTo);

    const matchedPreset = findMatchingPreset(nextFrom, nextTo, presets);
    if (matchedPreset) {
      onPresetSelect?.(matchedPreset.name);
    }
  };

  const handlePickerOpenChange = (open: boolean) => {
    if (open) {
      onOpen?.();
    }
  };

  // Hide default inputs/separator so only the custom label (below) is visible, open or closed.
  const rangePickerClassName = twMerge(
    'w-full h-10',
    '[&_.ant-picker-input]:opacity-0',
    '[&_.ant-picker-range-separator]:opacity-0',
    '[&_.ant-picker-active-bar]:opacity-0'
  );

  return (
    <div className="relative w-full date-picker-echo" data-testid="date-picker-trigger">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 right-10 z-[1] flex items-center gap-2 text-left text-sm text-textDarkGrey"
      >
        <CalendarOutlined className="size-4 shrink-0 text-base leading-none" />
        <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
          <span className="hidden sm:inline">{prefix}</span>
          {body}
        </span>
      </div>
      <DatePicker.RangePicker
        aria-label={`Selected date range: ${prefix}${body}`}
        className={rangePickerClassName}
        disabledDate={isDateDisabled}
        presets={rangePresets}
        suffixIcon={<DownOutlined className="shrink-0 text-xs leading-none" />}
        value={[dayjs(from), dayjs(to)]}
        onChange={handleRangeChange}
        onOpenChange={handlePickerOpenChange}
      />
    </div>
  );
};

export default DateRangeFilter;
